import { Skeleton } from "@/components/ui/alert"
import { StatCard } from "@/components/ui/stat-card"

import type { ThreadCounts } from "./api"
import { STATUS_LABEL, STATUS_TONE, type QueueStatus } from "./queue-data"

const TILES = Object.keys(STATUS_LABEL) as QueueStatus[]

/**
 * The triage strip: four counts from `/threads/summary`, over the same filters
 * as the list below it.
 *
 * Three states, as in the legacy queue's `CountTile`: a skeleton while the
 * count is unknown (never a 0, which is a number someone acts on), the count,
 * or a dash with a message once the request has failed — a skeleton left
 * pulsing over an error would promise an answer that is not coming.
 *
 * Display-only: a tile groups several review statuses, and the Status filter
 * takes exactly one, so a tile has no single filter to apply. Legacy's tiles
 * are not clickable either.
 */
export function QueueStatTiles({
  counts,
  failed,
}: {
  counts: ThreadCounts | undefined
  failed: boolean
}): JSX.Element {
  return (
    <section aria-label="Queue totals" className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
        {TILES.map((tile) => (
          <StatCard
            key={tile}
            value={
              failed ? (
                <span title="Unavailable">—</span>
              ) : counts ? (
                counts[tile]
              ) : (
                <Skeleton className="h-6 w-10 rounded" />
              )
            }
            label={STATUS_LABEL[tile]}
            tone={STATUS_TONE[tile]}
          />
        ))}
      </div>
      {failed && (
        <p role="status" className="text-body-sm text-text-3">
          Queue totals unavailable. The list below is unaffected.
        </p>
      )}
    </section>
  )
}
